import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  EgressLinkListEntry,
  EgressNodeTrafficRow,
  EgressPathSnapshotRow,
  EgressPathSwitchEventRecord,
  EgressLinkStatus,
  ReplicationHealthSample,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  listEgressLinks,
  listEgressNodeTraffic,
  listEgressSnapshots,
  listEgressSwitchEvents,
  listReplicationHealth,
  patchEgressLinkDispatch,
  patchEgressLinkStatus,
} from './egressLines.ts';

export const EGRESS_LINES_QUERY_KEY = ['egress-lines'] as const;

export function useEgressLinks(): UseQueryResult<readonly EgressLinkListEntry[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...EGRESS_LINES_QUERY_KEY, 'links'],
    queryFn: ({ signal }) => listEgressLinks(fetchImpl, signal),
  });
}

export function useEgressSnapshots(): UseQueryResult<readonly EgressPathSnapshotRow[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...EGRESS_LINES_QUERY_KEY, 'snapshots'],
    queryFn: ({ signal }) => listEgressSnapshots(fetchImpl, signal),
  });
}

export function useEgressSwitchEvents(): UseQueryResult<readonly EgressPathSwitchEventRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...EGRESS_LINES_QUERY_KEY, 'switch-events'],
    queryFn: ({ signal }) => listEgressSwitchEvents(fetchImpl, signal, 100),
  });
}

export function useEgressNodeTraffic(): UseQueryResult<readonly EgressNodeTrafficRow[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...EGRESS_LINES_QUERY_KEY, 'nodes'],
    queryFn: ({ signal }) => listEgressNodeTraffic(fetchImpl, signal),
  });
}

export function useReplicationHealth(): UseQueryResult<readonly ReplicationHealthSample[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...EGRESS_LINES_QUERY_KEY, 'replication-health'],
    queryFn: ({ signal }) => listReplicationHealth(fetchImpl, signal),
  });
}

export function usePatchEgressLinkStatus(): UseMutationResult<
  unknown,
  Error,
  { egressId: string; status: EgressLinkStatus }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ egressId, status }) => patchEgressLinkStatus(egressId, { status }, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_LINES_QUERY_KEY });
    },
  });
}

export function usePatchEgressLinkDispatch(): UseMutationResult<
  unknown,
  Error,
  { egressId: string; dispatchEnabled: boolean }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ egressId, dispatchEnabled }) =>
      patchEgressLinkDispatch(egressId, { dispatch_enabled: dispatchEnabled }, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_LINES_QUERY_KEY });
    },
  });
}
